import { useState } from "react";
import { Send, MapPin, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Validation schema for tour form data
const tourSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Name can only contain letters, spaces, hyphens, and apostrophes"),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  phone: z
    .string()
    .trim()
    .min(1, "Phone number is required")
    .regex(/^[\d\s()+-]+$/, "Please enter a valid phone number")
    .min(10, "Phone number must be at least 10 digits")
    .max(20, "Phone number is too long"),
  preferredDate: z
    .string()
    .min(1, "Please select a preferred date")
    .refine((date) => {
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, "Tour date must be in the future"),
  preferredTime: z
    .string()
    .min(1, "Please select a preferred time"),
  eventType: z
    .string()
    .optional(),
  message: z
    .string()
    .trim()
    .max(500, "Message must be less than 500 characters")
    .optional(),
});

type FormErrors = Partial<Record<keyof z.infer<typeof tourSchema>, string>>;

const TourScheduler = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferredDate: "",
    preferredTime: "",
    eventType: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = (): boolean => {
    try {
      tourSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          if (!newErrors[field]) {
            newErrors[field] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('submit-booking', {
        body: {
          ...formData,
          eventType: 'vendor-tour',
          isTourRequest: true,
        },
      });

      if (error) {
        throw error;
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        preferredDate: "",
        preferredTime: "",
        eventType: "",
        message: "",
      });
    } catch (error: any) {
      console.error("Submission error:", error);
      
      let userMessage = "Unable to submit tour request. Please try again or call us at (770) 441-0401.";
      if (error?.context?.status === 429) {
        userMessage = "You've reached the submission limit. Please try again later.";
      } else if (error?.context?.status === 400) {
        userMessage = "Please check your information and try again.";
      }
      
      toast({
        title: "Submission Failed",
        description: userMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const ErrorMessage = ({ field }: { field: keyof FormErrors }) => {
    if (!errors[field]) return null;
    return (
      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {errors[field]}
      </p>
    );
  };

  if (isSubmitted) {
    return (
      <section id="tour" className="section-padding bg-muted">
        <div className="container-elegant max-w-2xl text-center">
          <div className="bg-card rounded-2xl p-12 shadow-elevated">
            <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-accent" />
            </div>
            <h3 className="font-serif text-3xl font-semibold text-foreground mb-4">
              Tour Request Received!
            </h3>
            <p className="text-muted-foreground text-lg mb-6">
              Thank you for your interest in The Pardue Center! We'll contact you within 24 hours to confirm your tour appointment.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="text-primary font-medium hover:underline"
            >
              Schedule another tour
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="tour" className="section-padding bg-muted">
      <div className="container-elegant">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Left Column - Info */}
          <div>
            <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">
              See Our Venue in Person
            </p>
            <h2 className="heading-section text-foreground mb-6">
              Schedule a Vendor Tour
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              Experience the beauty and versatility of The Pardue Family Foundation Event Center firsthand. 
              Walk through our elegant ballroom, outdoor pavilion, and conference spaces while we discuss 
              how we can bring your event vision to life.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">
                    205 Cox Road, Roswell, GA 30075
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    Convenient North Atlanta location
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">
                    Tours by Appointment
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    Weekdays & weekends available
                  </p>
                </div>
              </div>
            </div>

            {/* Tour highlights */}
            <div className="mt-8 p-6 bg-card rounded-xl border border-border/50">
              <h4 className="font-semibold text-foreground mb-4">What to Expect</h4>
              <ul className="space-y-2 text-muted-foreground text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span>Guided walkthrough of all event spaces</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span>Discuss your event vision and needs</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span>Learn about our catering and menu options</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span>Get pricing information and availability</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-card rounded-2xl p-8 shadow-elevated">
            <h3 className="font-serif text-2xl font-medium text-foreground mb-6">
              Request Your Tour
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label
                  htmlFor="tour-name"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Your Name *
                </label>
                <input
                  type="text"
                  id="tour-name"
                  name="name"
                  required
                  maxLength={100}
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                    errors.name ? "border-red-500" : "border-border"
                  }`}
                  placeholder="Jane Smith"
                />
                <ErrorMessage field="name" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="tour-email"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="tour-email"
                    name="email"
                    required
                    maxLength={255}
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      errors.email ? "border-red-500" : "border-border"
                    }`}
                    placeholder="jane@example.com"
                  />
                  <ErrorMessage field="email" />
                </div>
                <div>
                  <label
                    htmlFor="tour-phone"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="tour-phone"
                    name="phone"
                    required
                    maxLength={20}
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      errors.phone ? "border-red-500" : "border-border"
                    }`}
                    placeholder="(555) 123-4567"
                  />
                  <ErrorMessage field="phone" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="tour-date"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    id="tour-date"
                    name="preferredDate"
                    required
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.preferredDate}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      errors.preferredDate ? "border-red-500" : "border-border"
                    }`}
                  />
                  <ErrorMessage field="preferredDate" />
                </div>
                <div>
                  <label
                    htmlFor="tour-time"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    Preferred Time *
                  </label>
                  <select
                    id="tour-time"
                    name="preferredTime"
                    required
                    value={formData.preferredTime}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      errors.preferredTime ? "border-red-500" : "border-border"
                    }`}
                  >
                    <option value="">Select a time</option>
                    <option value="morning">Morning (9am - 12pm)</option>
                    <option value="afternoon">Afternoon (12pm - 4pm)</option>
                    <option value="evening">Evening (4pm - 7pm)</option>
                  </select>
                  <ErrorMessage field="preferredTime" />
                </div>
              </div>

              <div>
                <label
                  htmlFor="tour-event-type"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  What type of event are you planning?
                </label>
                <select
                  id="tour-event-type"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                >
                  <option value="">Select an event type (optional)</option>
                  <option value="wedding">Wedding Reception</option>
                  <option value="quinceanera">Quinceañera</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="nonprofit">Nonprofit Gala</option>
                  <option value="shower">Shower / Rehearsal</option>
                  <option value="private">Private Celebration</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="tour-message"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Any questions or special requests?
                </label>
                <textarea
                  id="tour-message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none ${
                    errors.message ? "border-red-500" : "border-border"
                  }`}
                  placeholder="Let us know if you have any specific questions..."
                />
                <ErrorMessage field="message" />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-gold py-4 rounded-lg font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-70"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Request Tour <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TourScheduler;
